// src/components/ui/core/Button/Button.jsx
import React from 'react';
import { cn } from '../../../../utils/cn';

const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  loading = false, // Add loading prop to prevent it from spreading to DOM
  disabled = false,
  icon: Icon,
  iconPosition = 'left',
  className = '',
  as: Component = 'button',
  ...props
}) => {
  // Handle both isLoading and loading props
  const actualLoading = isLoading || loading;

  // Base button styles that apply to all variants
  const baseStyles = cn(
    "relative inline-flex items-center justify-center font-semibold",
    "transition-all duration-200 ease-out transform",
    "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900",
    "disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none",
    "hover:scale-105 active:scale-95"
  );

  // Size variants
  const sizeStyles = {
    sm: "px-3 py-2 text-sm gap-1.5",
    md: "px-4 py-3 text-base gap-2",
    lg: "px-6 py-4 text-lg gap-2.5"
  };

  // Button variants matching the login page styles
  const variantStyles = {
    // Primary - Login button gradient
    primary: cn(
      "bg-gradient-to-r from-blue-600 to-purple-600",
      "text-white",
      "hover:from-blue-500 hover:to-purple-500",
      "focus:ring-blue-500/50",
      "shadow-lg shadow-blue-500/25",
      "rounded-xl"
    ),
    
    // Secondary - Partner signup link button
    secondary: cn(
      "bg-slate-800/50 border border-slate-600/50",
      "text-slate-300",
      "hover:bg-slate-700/50 hover:border-slate-500/50 hover:text-slate-200",
      "focus:ring-emerald-500/50",
      "rounded-xl"
    ),
    
    // Success - Used in partner signup
    success: cn(
      "bg-gradient-to-r from-emerald-600 to-blue-600",
      "text-white",
      "hover:from-emerald-500 hover:to-blue-500",
      "focus:ring-emerald-500/50",
      "shadow-lg shadow-emerald-500/25",
      "rounded-xl"
    ),
    
    // Ghost - Subtle button variant
    ghost: cn(
      "bg-transparent border border-transparent",
      "text-slate-400 hover:text-slate-300",
      "hover:bg-slate-800/30",
      "focus:ring-slate-500/50",
      "rounded-xl"
    ),
    
    // Danger - For destructive actions
    danger: cn(
      "bg-gradient-to-r from-red-600 to-red-500",
      "text-white",
      "hover:from-red-500 hover:to-red-400",
      "focus:ring-red-500/50",
      "shadow-lg shadow-red-500/25",
      "rounded-xl"
    )
  };

  // Determine if button should be disabled - use actualLoading
  const isDisabled = disabled || actualLoading;

  // Handle icon rendering - use actualLoading
  const renderIcon = () => {
    if (actualLoading) {
      // Loading spinner icon
      return (
        <svg 
          className="animate-spin" 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="none"
        >
          <circle 
            cx="12" 
            cy="12" 
            r="10" 
            stroke="currentColor" 
            strokeWidth="4" 
            className="opacity-25"
          />
          <path 
            fill="currentColor" 
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            className="opacity-75"
          />
        </svg>
      );
    }
    
    if (Icon) {
      return <Icon className="w-5 h-5" />;
    }
    
    return null;
  };

  return (
    <Component
      className={cn(
        baseStyles,
        sizeStyles[size],
        variantStyles[variant],
        className
      )}
      disabled={isDisabled}
      {...props}
    >
      {/* Left icon */}
      {iconPosition === 'left' && renderIcon()}
      
      {/* Button text - use actualLoading */}
      {actualLoading ? (
        typeof children === 'string' && children.includes('...') 
          ? children 
          : `${children}...`
      ) : (
        children
      )}
      
      {/* Right icon - use actualLoading */}
      {iconPosition === 'right' && !actualLoading && renderIcon()}
    </Component>
  );
};

export default Button;